import {
  AttachmentsList,
  EditorAudio,
  EditorAudioAnswer,
  EditorBlockQuote,
  EditorCategory,
  EditorCollapsibleBox,
  EditorConfirmation,
  EditorDivider,
  editorDividerPropsSchema,
  EditorFileUpload,
  EditorGallery,
  EditorHotspot,
  EditorHotspotAnswer,
  EditorHotspotLabel,
  EditorImage,
  EditorInstruction,
  editorInstructionPropsSchema,
  EditorMatchingList,
  EditorMultipleChoice,
  EditorMultistep,
  EditorQuestionContainer,
  EditorSectionHeader,
  editorSectionHeaderPropsSchema,
  EditorSketchpad,
  EditorSortableList,
  EditorSupplementalContent,
  EditorTable,
  EditorText,
  EditorTextAnswer,
  EditorThreeColumn,
  EditorTimeline,
  EditorTwoColumn,
  EditorVideo,
  EditorVideoAnswer,
} from "@cca/components";
import { type ContentAreaComponentDtoWithName } from "@core/types";
import { useLearnerFileProvider } from "@providers";
import { groupBy, mapValues } from "remeda";
import { z } from "zod";

export type ObjectWithId = { id: PropertyKey };

export type OrderedObjectWithId = ObjectWithId & { displaySequence: number };

export type NodesByParentIdRecord<TNode extends ObjectWithId> = Record<
  PropertyKey,
  TNode[]
>;

export type NodeArray<TNode extends ObjectWithId> = (
  | TNode
  | (TNode & {
      children: NodeArray<TNode>;
      slots: Record<string, NodeArray<TNode>>;
    })
)[];

function hasContainerName<TObject extends object>(
  object: TObject,
): object is TObject & { containerName: string } {
  return "containerName" in object && typeof object.containerName === "string";
}

function sortNodes(nodes: OrderedObjectWithId[]) {
  nodes.sort(
    (firstNode, secondNode) =>
      firstNode.displaySequence - secondNode.displaySequence,
  );
}

export function nodesByParentIdToNodeArray<TNode extends OrderedObjectWithId>(
  nodesByParentId: NodesByParentIdRecord<TNode>,
  parentId: PropertyKey,
): NodeArray<TNode> {
  return (nodesByParentId[parentId] || []).map((node) => {
    if (nodesByParentId[node.id] === undefined) return node;

    const { children = [], ...slots } = groupBy(
      nodesByParentIdToNodeArray(nodesByParentId, node.id),
      (childNode) =>
        hasContainerName(childNode) ? childNode.containerName : "children",
    );

    Object.values(slots).forEach(sortNodes);
    sortNodes(children);

    return {
      ...node,
      slots,
      children,
    };
  });
}

export const editorComponentMap = new Map([
  ["EditorAttachments", { element: AttachmentsList }],
  ["EditorAudio", { element: EditorAudio }],
  [
    "EditorAudioAnswer",
    {
      element: EditorAudioAnswer,
      providers: {
        useLearnerFileProvider,
      },
    },
  ],
  ["EditorBlockQuote", { element: EditorBlockQuote }],
  ["EditorCategory", { element: EditorCategory }],
  ["EditorCollapsibleBox", { element: EditorCollapsibleBox }],
  ["EditorConfirmation", { element: EditorConfirmation }],
  [
    "EditorDivider",
    {
      element: EditorDivider,
      propsSchema: editorDividerPropsSchema,
    },
  ],
  [
    "EditorFileUpload",
    {
      element: EditorFileUpload,
      providers: {
        useLearnerFileProvider,
      },
    },
  ],
  ["EditorGallery", { element: EditorGallery }],
  ["EditorHotspot", { element: EditorHotspot }],
  ["EditorHotspotAnswer", { element: EditorHotspotAnswer }],
  ["EditorHotspotLabel", { element: EditorHotspotLabel }],
  ["EditorImage", { element: EditorImage }],
  [
    "EditorInstruction",
    { element: EditorInstruction, propsSchema: editorInstructionPropsSchema },
  ],
  ["EditorMultipleChoice", { element: EditorMultipleChoice }],
  ["EditorMultistep", { element: EditorMultistep }],
  ["EditorQuestionContainer", { element: EditorQuestionContainer }],
  ["EditorRelationalList", { element: EditorMatchingList }],
  [
    "EditorSectionHeader",
    {
      element: EditorSectionHeader,
      propsSchema: editorSectionHeaderPropsSchema,
    },
  ],
  ["EditorSketchpad", { element: EditorSketchpad }],
  ["EditorSortableList", { element: EditorSortableList }],
  ["EditorSupplementalContent", { element: EditorSupplementalContent }],
  ["EditorTable", { element: EditorTable }],
  [
    "EditorText",
    {
      element: EditorText,
      propsSchema: z.object({
        value: z.string(),
      }),
    },
  ],
  ["EditorTextAnswer", { element: EditorTextAnswer }],
  ["EditorTimeline", { element: EditorTimeline }],
  ["EditorTwoColumn", { element: EditorTwoColumn }],
  ["EditorThreeColumn", { element: EditorThreeColumn }],
  ["EditorVideo", { element: EditorVideo }],
  [
    "EditorVideoAnswer",
    {
      element: EditorVideoAnswer,
      providers: {
        useLearnerFileProvider,
      },
    },
  ],
]);

export const renderComponentsFromDtos = (
  dtos: NodeArray<ContentAreaComponentDtoWithName>,
) =>
  dtos.map((dto) => {
    const editorComponent = editorComponentMap.get(dto.componentName);

    if (!editorComponent) return null;

    const parsedProps = dto.settings && JSON.parse(dto.settings);
    const parseResult = editorComponent.propsSchema?.safeParse(parsedProps);
    const propsParsingError = parseResult?.error;

    if (!!propsParsingError) {
      console.error(propsParsingError);

      return null;
    }

    const renderedChildren =
      "children" in dto ? renderComponentsFromDtos(dto.children) : null;

    if ("slots" in dto) {
      Object.values(dto.slots).forEach(([slotFirstChild]) => {
        const slotFirstChildSettings = slotFirstChild.settings
          ? JSON.parse(slotFirstChild.settings)
          : {};

        slotFirstChildSettings.readSpeakerEnabled =
          parsedProps?.readSpeakerEnabled
            ? false
            : slotFirstChildSettings.readSpeakerEnabled;

        slotFirstChild.settings = JSON.stringify(slotFirstChildSettings);
      });
    }

    const renderedSlots =
      "slots" in dto ? mapValues(dto.slots, renderComponentsFromDtos) : null;

    const Element = editorComponent.element;

    return (
      <Element
        key={dto.id}
        {...parsedProps}
        componentId={dto.id}
        contentAreaVersionComponentUserId={
          dto.contentAreaVersionComponentUserId
        }
        providers={editorComponent.providers}
        questionDisplayNumber={dto.questionDisplayNumber}
        slots={renderedSlots}
      >
        {renderedChildren}
      </Element>
    );
  });
